import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GraduationCap, Globe, Award, Building2, Newspaper, Users } from 'lucide-react';

const credentials = [
  { icon: GraduationCap, text: 'Engineer + Business Graduate' },
  { icon: Globe, text: '11 African Nations' },
  { icon: Building2, text: 'Trusted Advisor to Governments' },
  { icon: Award, text: 'World Bank PRCG Collaboration' },
  { icon: Users, text: 'Africa Cup of Nations 2008' },
  { icon: Newspaper, text: 'Times of Africa Founder' },
];

const countries = [
  'Nigeria', 'Zimbabwe', 'Ghana', 'Kenya', 'Tanzania',
  'Uganda', 'Zanzibar', 'Ethiopia', 'Senegal', 'Congo Brazzaville', 'DR Congo'
];

export const AboutSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section 
      id="about" 
      className="section relative"
      data-testid="about-section"
    >
      <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-[#D4AF37]/5 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Who is Kirit Sobti?</p>
            <h2 className="section-title mb-6">
              Personal Story <br />
              <span className="gradient-text">& Trust</span>
            </h2>
            
            {/* Rewritten Story Paragraph */}
            <div className="space-y-6 text-white/70 leading-relaxed">
              <p>
                In 2001, I arrived in Africa with a business plan, an engineering background, and more ambition than certainty. Over the next 30 years, I built companies in 11 countries — in ICT, healthcare, agriculture, media, and climate tech.
              </p>
              <p>
                I didn't just advise from the sidelines. I was in the room. I dealt with the governments, the investors, the setbacks, and the breakthroughs.
              </p>
              <p className="text-white/90 font-medium">
                Today, I use everything I've learned to help entrepreneurs like you — whether you're exploring Africa for the first time, scaling an existing business, or sitting on an idea you don't know how to start.
              </p>
            </div>

            {/* Personal Mission Quote */}
            <motion.blockquote
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4 }}
              className="mt-8 pl-6 border-l-2 border-[#D4AF37]"
            >
              <p className="text-xl italic text-white/90 font-['Playfair_Display']">
                "To empower those who are building sustainable businesses, innovative technologies, and inclusive communities."
              </p>
              <cite className="text-sm text-[#D4AF37] mt-2 block not-italic">— Kirit Sobti</cite>
            </motion.blockquote>

            {/* Credentials Grid */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 }}
              className="mt-8 grid grid-cols-2 gap-3"
            >
              {credentials.map((cred, index) => (
                <div 
                  key={cred.text}
                  className="flex items-center gap-2 text-sm text-white/60"
                >
                  <cred.icon size={16} className="text-[#D4AF37]" />
                  <span>{cred.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Image & Countries */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Portrait */}
            <div 
              className="relative aspect-[4/3] rounded-2xl overflow-hidden glass-light"
              data-testid="about-portrait"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/t4pmqqjv_WhatsApp%20Image%202026-04-05%20at%205.11.44%20PM.jpeg"
                alt="Kirit Sobti Portrait"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[#D4AF37] text-sm tracking-widest uppercase">Engineer • Entrepreneur • Coach</p>
              </div>
            </div>

            {/* Countries - Highlighted Pills */}
            <div>
              <p className="text-white/50 text-sm uppercase tracking-widest mb-4">Countries Active In</p>
              <div className="flex flex-wrap gap-2">
                {countries.map((country, index) => (
                  <motion.span
                    key={country}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.6 + index * 0.05 }}
                    className="country-tag"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#D4AF37] mr-2" />
                    {country}
                  </motion.span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
